import {
	type Collision,
	type CollisionDetection,
	closestCenter,
	pointerWithin,
} from "@dnd-kit/core";

import {
	FORM_BUILDER_DROP_ROOT,
	FORM_BUILDER_DROP_SECTION_PREFIX,
} from "./form-builder-drop-target";

/** Matches `PALETTE_PREFIX` in `components/form-builder/form-builder-panel` (palette drags only). */
const FORM_BUILDER_PALETTE_PREFIX = "palette::";

function isFormBuilderContainerDrop(id: string): boolean {
	if (id === FORM_BUILDER_DROP_ROOT) return true;
	if (id.startsWith(FORM_BUILDER_DROP_SECTION_PREFIX)) return true;
	return false;
}

function withoutContainerDrops(collisions: Collision[]): Collision[] {
	return collisions.filter((c) => !isFormBuilderContainerDrop(String(c.id)));
}

/**
 * Prefer nearest sortable by distance when @dnd-kit provides `data.value` on collisions.
 */
function sortCollisionsByDataValue(collisions: Collision[]): Collision[] {
	if (collisions.length <= 1) return collisions;
	return [...collisions].sort((a, b) => {
		const av =
			typeof a.data === "object" &&
			a.data !== null &&
			"value" in a.data &&
			typeof (a.data as { value: unknown }).value === "number"
				? (a.data as { value: number }).value
				: Number.POSITIVE_INFINITY;
		const bv =
			typeof b.data === "object" &&
			b.data !== null &&
			"value" in b.data &&
			typeof (b.data as { value: unknown }).value === "number"
				? (b.data as { value: number }).value
				: Number.POSITIVE_INFINITY;
		return av - bv;
	});
}

/**
 * Prefer sortable targets (fields, root rows) over large container droppables (`drop-root`,
 * `drop-section::*`). Otherwise the section wrapper always wins `pointerWithin` and `over`
 * resolves to append. When the pointer only hits a container (e.g. gaps between rows), use
 * `closestCenter` so the nearest field drives insert order (see dnd-kit nested droppable guidance).
 */
export const formBuilderCollisionDetection: CollisionDetection = (args) => {
	const activeId = String(args.active.id);
	const isPaletteDrag = activeId.startsWith(FORM_BUILDER_PALETTE_PREFIX);

	const pointerCollisions = pointerWithin(args);
	if (pointerCollisions.length > 0) {
		const nonContainers = withoutContainerDrops(pointerCollisions);
		if (nonContainers.length > 0) {
			return sortCollisionsByDataValue(nonContainers);
		}
		if (!isPaletteDrag) {
			const fromCenter = withoutContainerDrops(closestCenter(args));
			if (fromCenter.length > 0) {
				return sortCollisionsByDataValue(fromCenter);
			}
		}
		return sortCollisionsByDataValue(pointerCollisions);
	}

	const fromCenter = withoutContainerDrops(closestCenter(args));
	if (fromCenter.length > 0) {
		return sortCollisionsByDataValue(fromCenter);
	}
	return sortCollisionsByDataValue(closestCenter(args));
};
